<template>
  <label
    class="input-date"
    v-bind:class="tags"
    v-bind:style="{ maxWidth: maxWidth }">
    <span
      class="input-date__label"
      v-if="!labelHidden">{{ label }}</span>
    <input
      class="input-date__input"
      v-bind:class="`input-date__input--${variant}`"
      data-lpignore="true"
      :pattern="pattern"
      :placeholder="mask"
      ref="input"
      type="text"
      inputmode="numeric"
      v-bind:aria-invalid="hasViolations"
      v-bind:disabled="disabled"
      v-bind:required="!optional"
      v-bind:title="title"
      v-model="maskedValue"
      v-on:input="onInput"
      v-on:invalid.prevent="onInvalid"
      v-on:change="onChange"
      v-on:keydown="onKeydown"
      v-on:blur="onBlur" />
    <p
      class="input-date__violation"
      role="alert"
      v-for="text in violations"
      v-bind:key="text"
    >{{ text }}</p>
    <p
      class="input-date__violation"
      role="alert"
      v-if="validityText"
    >{{ validityText }}</p>
  </label>
</template>
<script>

/**
 * @description Индекс первого разделителя
 * @type {number}
 */
const INDEX_DIVIDER_FIRST = 2;

/**
 * @description Индекс второго разделителя
 * @type {number}
 */
const INDEX_DIVIDER_SECOND = 5;

/**
 * @description Ввод даты.
 * @type {Vue}
 */
export default {
  computed: {
    /**
     * @description Имеет нарушения.
     * @type {boolean}
     */
    hasViolations() {
      return this.violations.length > 0 || this.validityText;
    },
    tags() {
      const list = [];
      if (this.violations.length > 0 || this.validityText) {
        list.push('invalid');
      }
      return list;
    },
    /**
     * @description Заголовок по наведению.
     * @type {(string|null)}
     */
    title() {
      if (this.labelHidden !== true) {
        return null;
      }
      return this.label;
    },
  },
  data() {
    return {
      maskedValue: this.applyMask(this.value),
      validityText: '',
    };
  },
  watch: {
    value(newValue) {
      this.maskedValue = this.applyMask(newValue);
    },
  },
  methods: {
    /**
     * @description Форматируем значение по маске "дд/мм/гггг"
     * @returns {string}
     */
    applyMask(value) {
      const newValue = value.replace(/\D/g, '');
      if (newValue === '') {
        return newValue;
      }
      const { mask } = this;
      let result = '';
      let valueIndex = 0;
      for (let i = 0; i < mask.length; i += 1) {
        if (/[дмг]/.test(mask[i])) {
          if (valueIndex < newValue.length) {
            result += newValue[valueIndex];
            valueIndex += 1;
          } else {
            result += mask[i];
          }
        } else {
          result += mask[i];
        }
      }
      return result;
    },
    /**
     * @description Обрабатываем изменения поля.
     * @returns {void}
     */
    onChange() {
      this.validityText = '';
    },
    /**
     * @description Обрабатываем ввод данных
     * @returns {void}
     */
    onInput(event) {
      const rawValue = event.target.value.replace(/\D/g, '');
      const newValue = this.getFormattedValue(rawValue);
      const { selectionStart } = event.srcElement;
      const { selectionEnd } = event.srcElement;
      this.$emit('input', newValue);
      this.$nextTick(() => {
        this.maskedValue = this.applyMask(newValue);
        this.moveCaretAtSelectionStartInput(selectionStart, selectionEnd);
      });
    },
    /**
     * @description Обрабатываем некорректность поля.
     * @returns {void}
     */
    onInvalid(event) {
      if (!event.target.validity.valid && !this.violations.length > 0) {
        this.validityText = 'Некорректный формат';
        this.$refs.input.scrollIntoView({ behavior: 'smooth' });
      }
    },
    /**
     * @description Удаляем строго с конца и корректно возвращаем маску.
     * @returns {void}
     */
    onKeydown(event) {
      if (event.key === 'Backspace') {
        event.preventDefault();
        const { selectionStart } = event.srcElement;
        const { selectionEnd } = event.srcElement;
        const shiftSelectionStart = this.getShiftOfMaskedInputIndex(selectionStart);
        const fixedSelectionStart = selectionStart - shiftSelectionStart;
        const shiftSelectionEnd = this.getShiftOfMaskedInputIndex(selectionEnd);
        const fixedSelectionEnd = selectionEnd - shiftSelectionEnd;
        const value = this.maskedValue.replace(/\D/g, '');
        if (value.length === 0) return;
        if (this.isCaretOnDividers(selectionStart, selectionEnd)) {
          this.moveCaretAtSelectionStart(selectionStart, selectionEnd);
          return;
        }
        const caretAtTheEnd = selectionStart === event.target.value.length
          && selectionEnd === event.target.value.length;
        let newValue;
        if (caretAtTheEnd) {
          newValue = this.getFormattedValue(value.slice(0, -1));
        } else if (fixedSelectionStart === fixedSelectionEnd) {
          newValue = this.getFormattedValue(
            value.slice(0, fixedSelectionStart - 1) + value.slice(fixedSelectionEnd),
          );
        } else {
          newValue = this.getFormattedValue(
            value.slice(0, fixedSelectionStart) + value.slice(fixedSelectionEnd),
          );
        }
        this.$emit('input', newValue);
        this.$nextTick(() => {
          this.maskedValue = this.applyMask(newValue);
          this.moveCaretAtSelectionStart(selectionStart, selectionEnd);
        });
      }
    },
    /**
     * @description Приводим данные к формату "дд/мм/гггг"
     * @returns {string}
     */
    getFormattedValue(value) {
      const parts = value.match(/(\d{0,2})(\d{0,2})(\d{0,4})/);
      return `${parts[1] ? parts[1] : 'дд'}/${parts[2] ? parts[2] : 'мм'}/${parts[3] ? parts[3] : 'гггг'}`;
    },
    /**
     * @description Получить смещение индекса на разряды маски
     * @returns {number}
     */
    getShiftOfMaskedInputIndex(index) {
      if (index <= INDEX_DIVIDER_FIRST) return 0;
      if (index <= INDEX_DIVIDER_SECOND) return 1;
      return 2;
    },
    /**
     * @description Вернуть каретку на выбранные позиции
     * @returns {number}
     */
    moveCaretAtSelectionStart(selectionStart, selectionEnd) {
      if (selectionStart === selectionEnd) {
        this.$refs.input.selectionEnd = selectionStart - 1;
      } else {
        this.$refs.input.selectionEnd = selectionStart;
      }
    },
    /**
     * @description Вернуть каретку на выбранные позиции
     * @returns {number}
     */
    moveCaretAtSelectionStartInput(selectionStart, selectionEnd) {
      if (selectionStart === selectionEnd) {
        const shift = selectionStart === INDEX_DIVIDER_FIRST
        || selectionStart === INDEX_DIVIDER_SECOND ? 1 : 0;
        this.$refs.input.selectionEnd = selectionStart + shift;
      } else {
        this.$refs.input.selectionEnd = selectionStart;
      }
    },
    /**
     * @description Проверка нахождения каретки на разделителях
     * @returns {boolean}
     */
    isCaretOnDividers(selectionStart, selectionEnd) {
      return (
        selectionStart === INDEX_DIVIDER_FIRST + 1 && selectionEnd === INDEX_DIVIDER_FIRST + 1
      ) || (
        selectionStart === INDEX_DIVIDER_SECOND + 1 && selectionEnd === INDEX_DIVIDER_SECOND + 1
      ) || (
        selectionStart === INDEX_DIVIDER_FIRST && selectionEnd === INDEX_DIVIDER_FIRST + 1
      ) || (
        selectionStart === INDEX_DIVIDER_SECOND && selectionEnd === INDEX_DIVIDER_SECOND + 1
      );
    },
    /**
     * @description Обработать потерю фокуса.
     * @param {InputEvent} event Событие.
     * @param event
     */
    onBlur(event) {
      this.$emit('blur', event.target.value);
    },
  },
  name: 'InputDate',
  props: {
    /**
     * @description Стиль поля (default|underlined).
     * @type {string}
     */
    variant: {
      default: () => 'default',
      type: String,
    },
    /**
     * @description Максимальняа ширина.
     * @type {string}
     */
    maxWidth: {
      default: () => '',
      type: String,
    },
    /**
     * @description Отключен.
     */
    disabled: {
      default: () => false,
      type: Boolean,
    },
    /**
     * @description Заголовок.
     * @type {string}
     */
    label: {
      required: true,
      type: String,
    },
    /**
     * @description Заголовок должен быть скрыт.
     * @type {boolean}
     */
    labelHidden: {
      default: () => false,
      required: false,
      type: Boolean,
    },
    /**
     * @description Опционально.
     */
    optional: {
      default: () => true,
      type: Boolean,
    },
    /**
     * @description Значение.
     * @type {string}
     */
    value: {
      default: () => '',
      required: false,
      type: String,
    },
    /**
     * @description Список нарушений.
     * @type {string[]}
     */
    violations: {
      default: () => [],
      required: false,
      type: Array,
    },
    /**
     * @description Маска.
     * @type {string}
     *
     */
    mask: {
      type: String,
      required: false,
      default: () => 'дд/мм/гггг',
    },
    /**
     * @description Паттерн проверки
     * @type {string}
     */
    pattern: {
      type: String,
      required: false,
      default: () => '^[0-3]\\d\\/[01]\\d\\/\\d{4}$',
    },
  },
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";
@use "@/scss/fields";
.input-date {
  display: flex;
  flex-direction: column;
  &__label {
    color: var(--unfocus-color);
    font-size: .75rem;
    font-weight: 400;
    line-height: 1rem;
    margin: 0 0 2px 0;
  }
  &__input {
    &--default {
      @include fields.input-field();
    }
    &--underlined {
      @include fields.input-field-underlined(auto);
      cursor: pointer;
    }
  }
  &.invalid &__input {
    color: var(--warning-color);
  }
  &__violation {
    color: var(--warning-color);
    font-size: 12px;
    line-height: 14px;
    font-weight: 400;
    padding: .125rem;
    background-color: transparent !important;
  }
}
</style>
